import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Trash2, Edit3, AlertCircle, X } from 'lucide-react';

interface RecordingCardProps {
  id: string;
  title: string;
  duration: string;
  time: string;
  preview: string;
  onDelete?: (id: string) => Promise<boolean>;
}

const RecordingCard: React.FC<RecordingCardProps> = ({
  id,
  title,
  duration,
  time,
  preview,
  onDelete,
}) => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleCardClick = () => {
    navigate(`/editor?mode=edit&id=${encodeURIComponent(id)}&title=${encodeURIComponent(title)}&content=${encodeURIComponent(preview)}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      transition={{
        type: "spring",
        stiffness: 260,
        damping: 20
      }}
      className="relative group h-full"
    >
      {/* Card Shadow Layer */}
      <motion.div
        className="absolute inset-0 rounded-3xl bg-gradient-to-br from-sky-100/50 to-blue-100/50 blur-xl"
        animate={{
          opacity: isHovered ? 0.6 : 0,
          scale: isHovered ? 1.05 : 1
        }}
        transition={{ duration: 0.3 }}
      />
      
      {/* Main Card */}
      <div 
        onClick={handleCardClick}
        className="relative bg-white rounded-3xl overflow-hidden cursor-pointer h-full flex flex-col"
        style={{
          boxShadow: isHovered 
            ? '0 20px 40px -10px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.02)'
            : '0 4px 20px -4px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.02)'
        }}
      >
        {/* Animated Shine Effect */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              className="absolute inset-0 opacity-60 pointer-events-none z-20"
              initial={{ x: '-100%' }}
              animate={{ x: '200%' }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 1.2,
                ease: "easeInOut",
                repeat: Infinity,
                repeatDelay: 2
              }}
              style={{
                background: 'linear-gradient(105deg, transparent 40%, rgba(14, 165, 233, 0.2) 50%, transparent 60%)'
              }}
            />
          )}
        </AnimatePresence>

        {/* Top Section with Gradient Background */}
        <div className="relative bg-gradient-to-br from-gray-50 via-white to-gray-50 px-8 pt-6 pb-2">
          {/* Delete Button */}
          <AnimatePresence>
            {isHovered && !showDeleteConfirm && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8, rotate: -180 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                exit={{ opacity: 0, scale: 0.8, rotate: 180 }}
                whileHover={{ scale: 1.1, rotate: 10 }}
                whileTap={{ scale: 0.9 }}
                transition={{
                  type: "spring",
                  stiffness: 260,
                  damping: 20
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteConfirm(true);
                }}
                className="absolute top-3 right-6 w-10 h-10 rounded-full bg-gradient-to-br from-red-50 to-red-100 hover:from-red-100 hover:to-red-200 flex items-center justify-center shadow-lg z-10 group/delete"
              >
                <Trash2 className="w-4 h-4 text-red-500 group-hover/delete:text-red-600 transition-colors" strokeWidth={2} />
              </motion.button>
            )}
          </AnimatePresence>

          {/* Delete Confirmation */}
          <AnimatePresence>
            {showDeleteConfirm && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -10 }}
                className="absolute top-2 right-4 z-20"
                onClick={(e) => e.stopPropagation()}
              >
                <motion.div
                  className="bg-white rounded-2xl shadow-2xl p-4 min-w-[200px]"
                  style={{
                    boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.05)'
                  }}
                >
                  <div className="flex items-start space-x-3 mb-4">
                    <motion.div
                      animate={{ rotate: [0, -10, 10, -10, 0] }}
                      transition={{ duration: 0.5 }}
                      className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center flex-shrink-0"
                    >
                      <AlertCircle className="w-4 h-4 text-orange-600" strokeWidth={2} />
                    </motion.div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">Delete recording?</p>
                      <p className="text-xs text-gray-500 mt-0.5">This action cannot be undone</p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDeleteConfirm(false);
                      }}
                      className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
                    >
                      <X className="w-3 h-3 text-gray-400" strokeWidth={2} />
                    </button>
                  </div>
                  
                  <div className="flex space-x-2">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDeleteConfirm(false);
                      }}
                      className="flex-1 px-3 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium transition-colors"
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (onDelete && !isDeleting) {
                          setIsDeleting(true);
                          const success = await onDelete(id);
                          if (!success) {
                            setIsDeleting(false);
                            setShowDeleteConfirm(false);
                          }
                        }
                      }}
                      disabled={isDeleting}
                      className="flex-1 px-3 py-2 rounded-xl bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-medium transition-all shadow-lg"
                    >
                      {isDeleting ? 'Deleting...' : 'Delete'}
                    </motion.button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Title Section */}
          <div className="mt-10">
            <h3 className="text-lg font-medium text-gray-900 mb-0.5 line-clamp-1 sm:line-clamp-2">{title}</h3>
            <div className="text-xs text-gray-400 mb-1">
              <span>{time}</span>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="px-8 pb-8 flex-1">
          <p className="text-base text-gray-700 leading-relaxed line-clamp-4 whitespace-pre-line break-words overflow-hidden">
            {preview}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default RecordingCard;